// Cost Comparison Handler Class
class CostComparison {
  constructor() {
    this.elements = {
      // Motherferment costs
      mfMonthly: ["mf-monthly-cost", "mf-monthly-cost-display"],
      mfAnnual: ["annual-mf-cost"],

      // Alternative costs
      alternativesMonthly: ["alternatives-monthly-cost"],
      alternativesAnnual: ["annual-alternatives-cost"],

      // Savings
      savingsMonthly: ["savings-amount-display"],
      savingsAnnual: ["annual-savings"],

      // Percentage displays
      savingsPercentage: ["savings-percentage-display"],
      savingsAnnualPercentage: [
        "annual-savings-percentage",
        "annual-percentage-display",
      ],

      // Add cans needed display
      cansNeeded: ["cans-needed-display"],
      concentrateNeeded: ["concentrate-needed-display"],
    };
  }

  // Update all elements of a specific type
  updateByType(type, value, options = {}) {
    const {
      prefix = "$",
      suffix = "",
      format = "currency",
      className = null,
      removeClasses = [],
    } = options;

    if (!this.elements[type]) {
      console.warn(`Cost comparison type "${type}" not found`);
      return;
    }

    this.elements[type].forEach((elementId) => {
      const element = document.getElementById(elementId);
      if (element) {
        // Format value based on type
        let formattedValue = value;
        if (format === "currency") {
          formattedValue = Math.abs(value).toFixed(0);
        } else if (format === "percentage") {
          formattedValue = Math.round(value);
        } else if (format === "decimal") {
          formattedValue = value.toFixed(1);
        } else if (format === "integer") {
          formattedValue = Math.round(value);
        }

        // Update content
        element.textContent = `${prefix}${formattedValue}${suffix}`;

        // Handle dynamic classes
        if (removeClasses.length > 0) {
          element.classList.remove(...removeClasses);
        }
        if (className) {
          element.classList.add(className);
        }
      }
    });
  }

  // Update progress indicators (like conic gradients)
  updateProgressIndicator(percentage) {
    // Update the CSS custom property for the conic gradient in both monthly and annual graphs
    const progressElements = document.querySelectorAll(
      '.monthly_graph [style*="--p"], .annual_graph [style*="--p"]'
    );
    progressElements.forEach((element) => {
      // Clamp percentage between 0 and 100
      const clampedPercentage = Math.max(
        0,
        Math.min(100, Math.round(Math.abs(percentage)))
      );
      element.style.setProperty("--p", clampedPercentage);
    });
  }

  // Update all cost comparisons at once
  updateAll(results) {
    // Monthly costs - using both ID and class-based updates
    this.updateByType("mfMonthly", results.totalMfCost);
    this.updateByClass("mf-monthly-cost", results.totalMfCost);

    this.updateByType("alternativesMonthly", results.totalCompetitorCost);
    this.updateByClass(
      "alternatives-monthly-cost",
      results.totalCompetitorCost
    );

    this.updateByType("savingsMonthly", results.totalSavings, {
      prefix: results.totalSavings >= 0 ? "+$" : "-$",
    });
    this.updateByClass("savings-monthly", results.totalSavings, {
      prefix: "",
    });

    // Annual costs
    this.updateByType("mfAnnual", results.totalMfCost * 12);
    this.updateByClass("mf-annual-cost", results.totalMfCost * 12);

    this.updateByType("alternativesAnnual", results.totalCompetitorCost * 12);
    this.updateByClass(
      "alternatives-annual-cost",
      results.totalCompetitorCost * 12
    );

    this.updateByType("savingsAnnual", results.totalSavings * 12, {
      prefix: results.totalSavings >= 0 ? "+$" : "-$",
    });
    this.updateByClass("savings-annual", results.totalSavings * 12, {
      prefix: results.totalSavings >= 0 ? "+$" : "-$",
    });

    // Percentage
    this.updateByType("savingsPercentage", results.savingsPercentage, {
      prefix: "",
      suffix: "% savings",
      format: "percentage",
    });
    this.updateByClass("savings-percentage", results.savingsPercentage, {
      prefix: "",
      suffix: "%",
      format: "percentage",
    });

    // Add cans needed updates
    this.updateByType("cansNeeded", results.containersNeeded, {
      prefix: "",
      suffix: "",
      format: "decimal",
    });
    this.updateByClass("cans-needed-display", results.containersNeeded, {
      prefix: "",
      suffix: "",
      format: "decimal",
    });

    // Add concentrate needed updates
    this.updateByType("concentrateNeeded", results.totalConcentrateOz, {
      prefix: "",
      suffix: "",
      format: "integer",
    });
    this.updateByClass(
      "concentrate-needed-display",
      results.totalConcentrateOz,
      {
        prefix: "",
        suffix: "",
        format: "integer",
      }
    );

    // Update progress indicators
    this.updateProgressIndicator(results.savingsPercentage);
  }

  // Update elements by CSS class
  updateByClass(className, value, options = {}) {
    const elements = document.querySelectorAll(`.${className}`);
    elements.forEach((element) => {
      const { prefix = "$", suffix = "", format = "currency" } = options;

      let formattedValue = value;
      if (format === "currency") {
        formattedValue = Math.abs(value).toFixed(0);
      } else if (format === "percentage") {
        formattedValue = Math.round(value);
      } else if (format === "decimal") {
        formattedValue = value.toFixed(1);
      } else if (format === "integer") {
        formattedValue = Math.round(value);
      }

      element.textContent = `${prefix}${formattedValue}${suffix}`;
    });
  }
}

// Motherferment Calculator with Modern UI Compatibility
class MotherfermentCalculator {
  constructor() {
    this.applications = [];
    this.selectedApplications = new Set();
    this.householdData = {
      adults: 1,
      babies: 0,
      dogs: 0,
      cars: 1,
    };

    // Motherferment pricing - will be loaded from settings
    this.containerPrice = 119.0;
    this.containerSizeOz = 64;
    this.costPerOz = 1.859375;

    // Initialize cost comparison handler
    this.costComparison = new CostComparison();

    this.init();
  }

  async init() {
    this.loadPricingData();
    this.loadApplicationData();
    this.setupEventListeners();
    this.preselectDefaultApplications();
    this.updateCalculations();

    // Make calculator available globally for modern UX layer
    window.calculator = this;
  }

  loadPricingData() {
    const pricingScript = document.getElementById("metaobject-pricing-data");
    if (pricingScript) {
      try {
        const data = JSON.parse(pricingScript.textContent);
        this.containerPrice = Number(data.container_price) || 119.0;
        this.containerSizeOz = Number(data.container_size_oz) || 64;
        this.costPerOz = this.containerPrice / this.containerSizeOz || 1.859375; // Convert from cents to dollars
      } catch (e) {
        console.warn("Failed to load pricing data, using defaults");
        console.log(e);
      }
    }
  }

  loadApplicationData() {
    const metaobjectScript = document.getElementById("metaobject-data");
    if (metaobjectScript) {
      try {
        const data = JSON.parse(metaobjectScript.textContent);

        this.applications = data.map((app) => ({
          name: app.name,
          category: app.category,
          monthly_usage: Number(app.monthly_usage),
          percentage_increase: Number(app.percentage_increase) / 100, // Convert from whole number to decimal
          scaling: app.scaling,
          concentrate_ratio: Number(app.concentrate_ratio) / 10, // Convert from x10 multiplier to actual ratio
          competitor_price: Number(app.competitor_price) / 100, // Convert from cents to dollars
          pre_selected: app.pre_selected,
        }));
      } catch (e) {
        this.applications = [];
      }
    } else {
      this.applications = [];
    }
  }

  setupEventListeners() {
    // Updated event listeners for new UI structure
    document.addEventListener("click", (e) => {
      if (e.target.matches('[data-action="select-all"]')) {
        const category = e.target.dataset.category;
        this.selectAllInCategory(category);
      } else if (e.target.matches('[data-action="clear-all"]')) {
        const category = e.target.dataset.category;
        this.clearAllInCategory(category);
      }
    });
  }

  preselectDefaultApplications() {
    // Helper to select all apps by scaling type
    const selectAppsByScaling = (scalingType) => {
      this.applications.forEach((app) => {
        if (app.scaling === scalingType) {
          const checkbox = document.querySelector(
            `[data-app-name="${app.name}"]`
          );
          if (checkbox) {
            checkbox.checked = true;
            const card = checkbox.closest(".product-card");
            if (card) card.classList.add("selected");
            this.selectedApplications.add(app.name);
          }
        }
      });
    };

    setTimeout(() => {
      // Pre-selected apps from Liquid
      this.applications.forEach((app) => {
        if (app.pre_selected) {
          const checkbox = document.querySelector(
            `[data-app-name="${app.name}"]`
          );
          if (checkbox && checkbox.checked) {
            this.selectedApplications.add(app.name);
          } else if (checkbox) {
            checkbox.checked = true;
            checkbox.closest(".product-card").classList.add("selected");
            this.selectedApplications.add(app.name);
          } else {
            console.warn(`Checkbox not found for: ${app.name}`);
          }
        }
      });

      // Map household keys to scaling types
      const scalingMap = [
        { key: "babies", scaling: "per-baby" },
        { key: "dogs", scaling: "per-dog" },
        { key: "cars", scaling: "per-car" },
      ];
      scalingMap.forEach(({ key, scaling }) => {
        if (this.householdData[key] > 0) {
          selectAppsByScaling(scaling);
        }
      });
    }, 100);
  }

  selectAllInCategory(category) {
    const categoryName = this.getCategoryName(category);
    this.applications
      .filter((app) => app.category === categoryName)
      .forEach((app) => {
        const checkbox = document.querySelector(
          `[data-app-name="${app.name}"]`
        );

        checkbox.checked = true;
        checkbox.closest(".product-card").classList.add("selected");
        this.selectedApplications.add(app.name);
      });

    this.updateCalculations();
  }

  clearAllInCategory(category) {
    const categoryName = this.getCategoryName(category);
    this.applications
      .filter((app) => app.category === categoryName)
      .forEach((app) => {
        const checkbox = document.querySelector(
          `[data-app-name="${app.name}"]`
        );
        checkbox.checked = false;
        checkbox.closest(".product-card").classList.remove("selected");
        this.selectedApplications.delete(app.name);
      });

    this.updateCalculations();
  }

  getCategoryName(category) {
    return category
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");
  }

  calculateFinishedProductUsage(app) {
    const { adults, babies, dogs, cars } = this.householdData;
    let usage = 0;

    // Handle special scaling cases per user instructions
    switch (app.scaling) {
      case "linear":
        if (app.name === "Body Wash") {
          // Body Wash scales with adults + children (babies)
          usage = app.monthly_usage * (adults + babies);
        } else {
          usage = app.monthly_usage * adults;
        }
        break;
      case "non-linear":
        // Uses percentage increase formula
        usage =
          app.monthly_usage * (1 + app.percentage_increase * (adults - 1));
        break;
      case "per-car":
        usage = app.monthly_usage * cars;
        break;
      case "per-baby":
        usage = app.monthly_usage * babies;
        break;
      case "per-dog":
        usage = app.monthly_usage * dogs;
        break;
      default:
        usage = app.monthly_usage;
    }

    return Math.max(0, usage);
  }

  calculateConcentrateNeeded(app, finishedProductUsage) {
    // CRITICAL: Special handling for laundry detergent (undiluted)
    if (app.special === "undiluted") {
      // Laundry detergent is used undiluted (1:1 ratio)
      return finishedProductUsage;
    } else {
      // Standard dilution calculation: (finished product / 24) * concentrate ratio
      return (finishedProductUsage / 24) * app.concentrate_ratio;
    }
  }

  updateCalculations() {
    let totalConcentrateOz = 0;
    let totalMfCost = 0;
    let totalCompetitorCost = 0;
    const breakdown = [];

    this.selectedApplications.forEach((appName) => {
      const app = this.applications.find((a) => a.name === appName);
      if (app) {
        const finishedProductUsage = this.calculateFinishedProductUsage(app);
        const concentrateNeeded = this.calculateConcentrateNeeded(
          app,
          finishedProductUsage
        );
        const mfCost = concentrateNeeded * this.costPerOz;
        const competitorCost = finishedProductUsage * app.competitor_price;
        const savings = competitorCost - mfCost;

        totalConcentrateOz += concentrateNeeded;
        totalMfCost += mfCost;
        totalCompetitorCost += competitorCost;

        breakdown.push({
          name: app.name,
          finishedProductUsage: finishedProductUsage,
          concentrateNeeded: concentrateNeeded,
          mfCost: mfCost,
          competitorCost: competitorCost,
          savings: savings,
          isSpecial: app.special === "undiluted",
        });
      }
    });

    // Decide recommended can size based on monthly concentrate need.
    // Rule: pick the smallest can that comfortably covers a 90-day subscription.
    // 33oz fits ~11 oz/month (33/3). Above that, recommend 64oz so quarterly reorders feel natural.
    const recommendedCanOz = totalConcentrateOz > 11 ? 64 : 33;
    // Use the recommended size for the displayed containers-needed + duration math,
    // so the customer-facing numbers match the variant that's about to auto-select.
    const effectiveSizeOz = recommendedCanOz;
    const containersNeeded = totalConcentrateOz / effectiveSizeOz;
    const totalSavings = totalCompetitorCost - totalMfCost;
    const savingsPercentage =
      totalCompetitorCost > 0 ? (totalSavings / totalCompetitorCost) * 100 : 0;

    this.updateUI({
      totalConcentrateOz,
      containersNeeded,
      containerSizeOz: effectiveSizeOz,
      recommendedCanOz,
      totalMfCost,
      totalCompetitorCost,
      totalSavings,
      savingsPercentage,
      breakdown,
    });

    // Notify variant picker (and other listeners) of the recommended can size.
    // Fired on every recalc — the variant picker dedupes to avoid fighting manual selections.
    document.dispatchEvent(new CustomEvent('calc-recommended-size', {
      detail: { recommendedCanOz: recommendedCanOz, monthlyConcentrateOz: totalConcentrateOz }
    }));
  }

  updateUI(results) {
    // Update legacy elements for compatibility with original design
    this.updateCanDisplay(results.containersNeeded, results.totalConcentrateOz);
    // Use the new CostComparison class to update all cost-related elements
    this.costComparison.updateAll(results);

    // Update breakdown table
    this.updateBreakdownTable(results.breakdown);
    // Update annual savings display

    // Notify modern UX layer if available
    if (window.modernUX) {
      setTimeout(() => window.modernUX.updateResultsDisplay(), 100);
    }
  }

  updateCanDisplay(containersNeeded, totalConcentrateOz) {
    // Update text displays for compatibility
    const cansNeededElement = document.getElementById("cans-needed-text");
    const concentrateElement = document.getElementById("total-concentrate-oz");

    if (cansNeededElement)
      cansNeededElement.textContent = containersNeeded.toFixed(1);
    if (concentrateElement)
      concentrateElement.textContent = Math.round(totalConcentrateOz);

    // The modern UX will handle the visual display
  }

  updateBreakdownTable(breakdown) {
    const tbody = document.getElementById("breakdown-table-body");
    if (!tbody) return;

    tbody.innerHTML = "";

    if (breakdown.length === 0) {
      tbody.innerHTML =
        '<tr><td colspan="5" style="text-align: center; color: #6b7280; padding: 3rem;">No applications selected</td></tr>';
      return;
    }

    const rowTemplate = document.getElementById("breakdown-row-template");
    if (!rowTemplate) {
      console.error("Breakdown row template not found");
      return;
    }

    breakdown.forEach((item, index) => {
      // Clone the template
      const rowClone = rowTemplate.content.cloneNode(true);

      // Populate product name and special indicator
      const productNameEl = rowClone.querySelector(".product-name");
      const specialIndicatorEl = rowClone.querySelector(".special-indicator");
      const specialNoteEl = rowClone.querySelector(".special-note");

      if (productNameEl) productNameEl.textContent = item.name;

      if (item.isSpecial) {
        if (specialIndicatorEl) specialIndicatorEl.textContent = " *";
        if (specialNoteEl) {
          specialNoteEl.classList.remove("hidden");
        }
      }

      // Populate usage amount
      const usageEl = rowClone.querySelector(".usage-amount");
      if (usageEl)
        usageEl.textContent = `${Math.round(item.finishedProductUsage)} oz`;

      // Populate costs
      const mfCostEl = rowClone.querySelector(".mf-cost");
      const competitorCostEl = rowClone.querySelector(".competitor-cost");
      if (mfCostEl) mfCostEl.textContent = `$${item.mfCost.toFixed(2)}`;
      if (competitorCostEl)
        competitorCostEl.textContent = `$${item.competitorCost.toFixed(2)}`;

      // Populate savings badge
      const savingsBadgeEl = rowClone.querySelector(".savings-badge");
      if (savingsBadgeEl) {
        const savingsPrefix = item.savings >= 0 ? "+" : "-";
        const savingsAmount = `${savingsPrefix}$${Math.abs(
          item.savings
        ).toFixed(2)}`;

        savingsBadgeEl.textContent = savingsAmount;

        // Add appropriate styling classes
        if (item.savings >= 0) {
          savingsBadgeEl.classList.add("text-accent", "text-green-800");
        } else {
          savingsBadgeEl.classList.add("text-black", "text-red-800");
        }
      }

      // Append the populated row to tbody
      tbody.appendChild(rowClone);
    });

    // Add totals row using template
    const totals = breakdown.reduce(
      (acc, item) => ({
        mfCost: acc.mfCost + item.mfCost,
        competitorCost: acc.competitorCost + item.competitorCost,
        savings: acc.savings + item.savings,
      }),
      { mfCost: 0, competitorCost: 0, savings: 0 }
    );

    const totalsTemplate = document.getElementById("totals-row-template");
    if (totalsTemplate) {
      const totalsClone = totalsTemplate.content.cloneNode(true);

      // Populate totals
      const mfTotalEl = totalsClone.querySelector(".mf-total");
      const competitorTotalEl = totalsClone.querySelector(".competitor-total");
      const totalsBadgeEl = totalsClone.querySelector(".totals-badge");

      if (mfTotalEl) mfTotalEl.textContent = `$${totals.mfCost.toFixed(2)}`;
      if (competitorTotalEl)
        competitorTotalEl.textContent = `$${totals.competitorCost.toFixed(2)}`;

      if (totalsBadgeEl) {
        const totalSavingsPrefix = totals.savings >= 0 ? "+" : "";
        totalsBadgeEl.textContent = `${totalSavingsPrefix}$${totals.savings.toFixed(
          2
        )}`;

        // Style the totals badge
        if (totals.savings >= 0) {
          totalsBadgeEl.classList.add("bg-green-500", "text-white");
          totalsBadgeEl.classList.remove("bg-red-500");
        } else {
          totalsBadgeEl.classList.add("bg-red-500", "text-white");
          totalsBadgeEl.classList.remove("bg-green-500");
        }
      }

      tbody.appendChild(totalsClone);
    }
  }
}

// Initialize the calculator when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new MotherfermentCalculator();
});
